import { appStorage } from './betaStorage'
import type { Role } from '../types'

export type DashboardWidgetId =
  | 'today_snapshot'
  | 'calendar'
  | 'team'
  | 'messages'
  | 'documents'
  | 'tasks'
  | 'routines'
  | 'shopping'
  | 'goals'
  | 'needs_cover'
  | 'updates'
  | 'about_me'
  | 'notifications'
  | 'funding_overview'
  | 'my_service_budget'

export interface DashboardPreferences {
  userId: string
  widgetIds: DashboardWidgetId[]
}

const STORAGE_KEY = 'schub.dashboard.v1'

const PARTICIPANT_HOME_CORE: DashboardWidgetId[] = [
  'team',
  'messages',
  'documents',
  'goals',
  'updates',
  'about_me',
]

const DEFAULTS: Record<Role, DashboardWidgetId[]> = {
  // Participant Home is the information hub. Day-to-day interactive actions
  // live on Today so the two screens have clearly different jobs.
  participant: [...PARTICIPANT_HOME_CORE],
  rep: ['today_snapshot', 'funding_overview', 'needs_cover', 'calendar', 'team', 'tasks', 'routines', 'updates'],
  support_worker: ['today_snapshot', 'calendar', 'tasks', 'routines', 'shopping', 'updates', 'messages'],
  allied_health: ['my_service_budget', 'calendar', 'goals', 'updates', 'documents', 'messages'],
  coordinator: ['funding_overview', 'needs_cover', 'calendar', 'goals', 'updates', 'documents'],
}

export const ALL_WIDGETS: { id: DashboardWidgetId; label: string; roles: Role[] }[] = [
  { id: 'today_snapshot', label: 'Today snapshot', roles: ['rep', 'support_worker'] },
  { id: 'calendar', label: 'Calendar', roles: ['rep', 'support_worker', 'allied_health', 'coordinator'] },
  { id: 'team', label: 'My Team', roles: ['participant', 'rep', 'support_worker', 'coordinator'] },
  { id: 'messages', label: 'Messages', roles: ['participant', 'rep', 'support_worker', 'allied_health', 'coordinator'] },
  { id: 'documents', label: 'Documents', roles: ['participant', 'rep', 'support_worker', 'allied_health', 'coordinator'] },
  { id: 'tasks', label: 'Tasks', roles: ['rep', 'support_worker'] },
  { id: 'routines', label: 'Routines', roles: ['rep', 'support_worker'] },
  { id: 'shopping', label: 'Shopping List', roles: ['support_worker'] },
  { id: 'goals', label: 'Goals', roles: ['participant', 'rep', 'allied_health', 'coordinator'] },
  { id: 'needs_cover', label: 'Needs Cover', roles: ['participant', 'rep', 'coordinator'] },
  { id: 'updates', label: 'Team Updates', roles: ['participant', 'rep', 'support_worker', 'allied_health', 'coordinator'] },
  { id: 'about_me', label: 'About Me', roles: ['participant', 'rep', 'support_worker', 'allied_health', 'coordinator'] },
  { id: 'funding_overview', label: 'Funding & whole plan budget', roles: ['participant', 'rep', 'coordinator'] },
  { id: 'my_service_budget', label: 'My service budget', roles: ['allied_health'] },
]

function loadAll(): DashboardPreferences[] {
  try {
    const raw = appStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export function allowedWidgetsForRole(role: Role) {
  return ALL_WIDGETS.filter((widget) => widget.roles.includes(role))
}

export function defaultWidgetsForRole(role: Role): DashboardWidgetId[] {
  return [...DEFAULTS[role]]
}

export function loadDashboardPreferences(userId: string, role: Role): DashboardPreferences {
  const saved = loadAll().find((item) => item.userId === userId)
  if (!saved) return { userId, widgetIds: defaultWidgetsForRole(role) }

  const allowed = new Set(allowedWidgetsForRole(role).map((item) => item.id))
  const filtered = saved.widgetIds.filter((id) => allowed.has(id))
  const defaults = defaultWidgetsForRole(role)

  // During the Home/Today split, older participant browsers may only have
  // Goals and Updates saved. Backfill the full information-hub set so all
  // existing beta profiles immediately receive the intended Home layout.
  if (role === 'participant') {
    for (const coreWidget of PARTICIPANT_HOME_CORE) {
      if (!filtered.includes(coreWidget)) filtered.push(coreWidget)
    }
  }

  if (role === 'allied_health' || role === 'coordinator') {
    for (const requiredFeed of ['goals', 'updates'] as DashboardWidgetId[]) {
      if (!filtered.includes(requiredFeed)) filtered.push(requiredFeed)
    }
  }

  return { userId, widgetIds: filtered.length ? filtered : defaults }
}

export function saveDashboardPreferences(preferences: DashboardPreferences) {
  const all = loadAll().filter((item) => item.userId !== preferences.userId)
  appStorage.setItem(STORAGE_KEY, JSON.stringify([...all, preferences]))
}

